import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api.service';
import { useTenantStore } from '../../stores/tenant.store';
import { usePermissions } from '../../context/PermissionsContext';
import {
  formatMoney,
  registerTypeLabel,
  humanizeTreasuryError,
  CTA_PRIMARY,
} from './treasuryUi';

type RegisterType = 'OPERATIVE' | 'BRANCH_MASTER' | 'CENTRAL_TREASURY';

const TYPE_CARDS: {
  type: RegisterType;
  title: string;
  description: string;
}[] = [
  {
    type: 'OPERATIVE',
    title: 'Caja operativa',
    description:
      'Es la caja que utiliza un cajero. Puede abrir y cerrar sesiones. Se usa para cobros y desembolsos en efectivo.',
  },
  {
    type: 'BRANCH_MASTER',
    title: 'Caja Madre',
    description:
      'Reserva de efectivo de una sucursal. Fondea y recibe fondos de cajas operativas. No tiene sesión de cajero.',
  },
  {
    type: 'CENTRAL_TREASURY',
    title: 'Tesorería Central',
    description:
      'Fondos centrales de la empresa. No pertenece a una sucursal operativa. No tiene sesión de cajero.',
  },
];

const TYPE_LABEL: Record<RegisterType, string> = {
  OPERATIVE: 'Caja operativa',
  BRANCH_MASTER: 'Caja Madre',
  CENTRAL_TREASURY: 'Tesorería Central',
};

const PERM_FLAGS: {
  key: 'canMove' | 'canOpen' | 'canClose' | 'canTransfer' | 'canApprove' | 'canAudit' | 'canAdjust';
  label: string;
}[] = [
  { key: 'canMove', label: 'Puede operar caja' },
  { key: 'canOpen', label: 'Puede abrir' },
  { key: 'canClose', label: 'Puede cerrar' },
  { key: 'canTransfer', label: 'Puede transferir' },
  { key: 'canApprove', label: 'Puede aprobar transferencias' },
  { key: 'canAudit', label: 'Puede supervisar arqueos' },
  { key: 'canAdjust', label: 'Puede solicitar sobrantes/faltantes' },
];

function personName(u?: { firstName?: string; lastName?: string } | null) {
  if (!u) return '—';
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';
}

/**
 * TX8 — ABM de cajas por tipo + permisos junto a la configuración.
 */
export function TreasuryCashRegistersPage() {
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const { can } = usePermissions();
  const canWrite = can('cash', 'write');
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  const [registers, setRegisters] = useState<any[]>([]);
  const [balances, setBalances] = useState<Record<string, number>>({});
  const [branches, setBranches] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [createStep, setCreateStep] = useState<'idle' | 'type' | 'form'>('idle');
  const [createType, setCreateType] = useState<RegisterType | null>(null);
  const [createForm, setCreateForm] = useState({
    branchId: '',
    number: '',
    name: '',
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', number: '' });

  const [permRegisterId, setPermRegisterId] = useState<string | null>(null);
  const [permRows, setPermRows] = useState<any[]>([]);
  const [permUserId, setPermUserId] = useState('');
  const [permFlags, setPermFlags] = useState<Record<string, boolean>>({
    canMove: true,
    canOpen: true,
    canClose: true,
    canTransfer: false,
    canApprove: false,
    canAudit: false,
    canAdjust: false,
  });

  const load = useCallback(async () => {
    if (!legalEntityId) return;
    setLoading(true);
    setError('');
    try {
      const [regs, summary, br, users] = await Promise.all([
        apiService.getCashRegisters(undefined, legalEntityId, { includeInactive: true }),
        apiService.getTreasurySummary(legalEntityId).catch(() => null),
        apiService.getBranches(),
        canWrite ? apiService.listStaffUsers().catch(() => []) : Promise.resolve([]),
      ]);
      setRegisters(regs ?? []);
      const bal: Record<string, number> = {};
      for (const r of summary?.registers ?? []) {
        bal[r.id] = Number(r.balance ?? 0);
      }
      setBalances(bal);
      setBranches(
        (br ?? []).filter((b: any) => !legalEntityId || b.legalEntityId === legalEntityId),
      );
      setStaff(users ?? []);
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  }, [legalEntityId, canWrite]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!focusId) return;
    const el = document.getElementById(`register-${focusId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusId, registers]);

  const grouped = useMemo(() => {
    const central = registers.filter((r) => r.type === 'CENTRAL_TREASURY');
    const madres = registers.filter((r) => r.type === 'BRANCH_MASTER');
    const ops = registers.filter((r) => r.type === 'OPERATIVE');
    return { central, madres, ops };
  }, [registers]);

  const resetCreate = () => {
    setCreateStep('idle');
    setCreateType(null);
    setCreateForm({ branchId: '', number: '', name: '' });
  };

  const submitCreate = async () => {
    if (!createType || !legalEntityId) return;
    if (!createForm.number.trim()) {
      setError('Indicá el número o código corto de la caja.');
      return;
    }
    if (createType !== 'CENTRAL_TREASURY' && !createForm.branchId) {
      setError('Seleccioná la sucursal.');
      return;
    }
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await apiService.createCashRegister({
        type: createType,
        number: createForm.number.trim(),
        name: createForm.name.trim() || undefined,
        ...(createType === 'CENTRAL_TREASURY'
          ? { legalEntityId }
          : { branchId: createForm.branchId }),
      });
      setSuccess(`${TYPE_LABEL[createType]} creada.`);
      resetCreate();
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setError(humanizeTreasuryError(Array.isArray(msg) ? msg.join(' ') : msg || e.message));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (r: any) => {
    setEditId(r.id);
    setEditForm({ name: r.name || '', number: r.number || '' });
    setSuccess('');
    setError('');
  };

  const saveEdit = async () => {
    if (!editId) return;
    setBusy(true);
    setError('');
    try {
      await apiService.updateCashRegister(editId, {
        name: editForm.name.trim() || undefined,
        number: editForm.number.trim(),
      });
      setEditId(null);
      setSuccess('Caja actualizada.');
      await load();
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (r: any) => {
    const next = !r.isActive;
    if (!next && r.sessions?.[0]) {
      setError('No se puede desactivar una caja con sesión abierta. Primero cerrá la sesión.');
      return;
    }
    if (
      !window.confirm(
        next
          ? `¿Reactivar ${r.name || r.code}?`
          : `¿Desactivar ${r.name || r.code}? Seguirá en el historial; no se elimina.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      await apiService.updateCashRegister(r.id, { isActive: next });
      setSuccess(next ? 'Caja reactivada.' : 'Caja desactivada.');
      await load();
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message));
    } finally {
      setBusy(false);
    }
  };

  const openPermissions = async (registerId: string) => {
    setPermRegisterId(registerId);
    setError('');
    try {
      const rows = await apiService.listCashPermissions(registerId);
      setPermRows(rows ?? []);
      setPermUserId('');
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message));
    }
  };

  const savePermission = async () => {
    if (!permRegisterId || !permUserId) return;
    setBusy(true);
    setError('');
    try {
      await apiService.upsertCashPermission({
        userId: permUserId,
        cashRegisterId: permRegisterId,
        ...permFlags,
      } as any);
      setSuccess('Permisos guardados.');
      const rows = await apiService.listCashPermissions(permRegisterId);
      setPermRows(rows ?? []);
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message));
    } finally {
      setBusy(false);
    }
  };

  const renderTable = (rows: any[], empty: string) => (
    <div className="overflow-x-auto border rounded-lg bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-left text-slate-600">
          <tr>
            <th className="px-3 py-2">Nombre / Número</th>
            <th className="px-3 py-2">Sucursal</th>
            <th className="px-3 py-2">Tipo</th>
            <th className="px-3 py-2">Estado</th>
            <th className="px-3 py-2">Saldo</th>
            <th className="px-3 py-2">Sesión</th>
            <th className="px-3 py-2">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-3 py-4 text-center text-slate-500">
                {empty}
              </td>
            </tr>
          )}
          {rows.map((r) => {
            const open = r.sessions?.[0];
            const type = r.type as RegisterType;
            return (
              <tr
                key={r.id}
                id={`register-${r.id}`}
                className={`border-t ${focusId === r.id ? 'bg-sky-50' : ''} ${
                  !r.isActive ? 'opacity-60' : ''
                }`}
              >
                <td className="px-3 py-2">
                  <div className="font-medium">{r.name || r.code}</div>
                  <div className="text-xs text-slate-500 font-mono">{r.number}</div>
                </td>
                <td className="px-3 py-2">{r.branch?.name || '—'}</td>
                <td className="px-3 py-2">{registerTypeLabel(type)}</td>
                <td className="px-3 py-2">{r.isActive ? 'Activa' : 'Inactiva'}</td>
                <td className="px-3 py-2 tabular-nums">
                  {balances[r.id] != null ? formatMoney(balances[r.id]) : '—'}
                </td>
                <td className="px-3 py-2">
                  {type !== 'OPERATIVE' ? (
                    <span className="text-slate-400">No aplica</span>
                  ) : open ? (
                    <span className="text-amber-800">
                      Abierta · {personName(open.user)}
                    </span>
                  ) : (
                    <span className="text-slate-500">Sin sesión</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {canWrite && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="text-blue-700 hover:underline"
                        onClick={() => openEdit(r)}
                      >
                        Editar
                      </button>
                      {type === 'OPERATIVE' && (
                        <button
                          type="button"
                          className="text-blue-700 hover:underline"
                          onClick={() => void openPermissions(r.id)}
                        >
                          Permisos
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-slate-700 hover:underline"
                        onClick={() => void toggleActive(r)}
                        disabled={busy}
                      >
                        {r.isActive ? 'Desactivar' : 'Reactivar'}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Cajas</h2>
          <p className="text-sm text-gray-600 mt-1">
            Alta y administración de Tesorería Central, Cajas Madre y Cajas Operativas. Las políticas
            de fondos están en{' '}
            <Link to="/treasury/config" className="text-blue-700 hover:underline">
              Configuración
            </Link>
            .
          </p>
        </div>
        {canWrite && createStep === 'idle' && (
          <button
            type="button"
            onClick={() => {
              setCreateStep('type');
              setError('');
              setSuccess('');
            }}
            className={CTA_PRIMARY}
          >
            + Nueva caja
          </button>
        )}
      </div>

      {!legalEntityId && (
        <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm">
          Seleccioná una empresa en la barra de contexto.
        </p>
      )}
      {error && (
        <p className="text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm">
          {error}
        </p>
      )}
      {success && (
        <p className="text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm">
          {success}
        </p>
      )}

      {createStep === 'type' && (
        <div className="border rounded-lg bg-white p-4 space-y-3">
          <h3 className="font-medium text-gray-900">¿Qué tipo de caja querés crear?</h3>
          <div className="grid md:grid-cols-3 gap-3">
            {TYPE_CARDS.map((c) => (
              <button
                key={c.type}
                type="button"
                onClick={() => {
                  setCreateType(c.type);
                  setCreateStep('form');
                }}
                className="text-left border rounded-lg p-4 hover:border-slate-900 hover:bg-slate-50 space-y-2"
              >
                <div className="font-semibold text-slate-900">{c.title}</div>
                <p className="text-sm text-slate-600">{c.description}</p>
              </button>
            ))}
          </div>
          <button type="button" onClick={resetCreate} className="text-sm text-slate-600 hover:underline">
            Cancelar
          </button>
        </div>
      )}

      {createStep === 'form' && createType && (
        <div className="border rounded-lg bg-white p-4 space-y-3 max-w-lg">
          <h3 className="font-medium text-gray-900">
            Nueva {TYPE_LABEL[createType]}
          </h3>
          <p className="text-xs text-slate-500">
            {TYPE_CARDS.find((c) => c.type === createType)?.description}
          </p>
          {createType !== 'CENTRAL_TREASURY' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal</label>
              <select
                required
                value={createForm.branchId}
                onChange={(e) => setCreateForm({ ...createForm, branchId: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Seleccionar…</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {createType === 'CENTRAL_TREASURY' && (
            <p className="text-sm text-slate-600 rounded-md bg-slate-50 border px-3 py-2">
              Se asigna a la empresa actual. No lleva sucursal.
            </p>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
            <input
              required
              value={createForm.number}
              onChange={(e) => setCreateForm({ ...createForm, number: e.target.value })}
              className="w-full border rounded-md px-3 py-2"
              placeholder={createType === 'CENTRAL_TREASURY' ? '01' : '2220'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre / alias (opcional)
            </label>
            <input
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              className="w-full border rounded-md px-3 py-2"
              placeholder="Ej. Caja mostrador 1"
            />
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={() => setCreateStep('type')}
              className="px-3 py-2 text-sm border rounded-md"
            >
              Cambiar tipo
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void submitCreate()}
              className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md disabled:opacity-50"
            >
              {busy ? 'Creando…' : 'Crear caja'}
            </button>
            <button type="button" onClick={resetCreate} className="px-3 py-2 text-sm text-slate-600">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {editId && (
        <div className="border rounded-lg bg-white p-4 space-y-3 max-w-lg">
          <h3 className="font-medium">Editar caja</h3>
          <p className="text-xs text-slate-500">
            El tipo y la sucursal no se cambian acá (protege la jerarquía).
          </p>
          <div>
            <label className="block text-sm font-medium mb-1">Número</label>
            <input
              value={editForm.number}
              onChange={(e) => setEditForm({ ...editForm, number: e.target.value })}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Nombre</label>
            <input
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              className="w-full border rounded-md px-3 py-2"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void saveEdit()}
              className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md"
            >
              Guardar
            </button>
            <button
              type="button"
              onClick={() => setEditId(null)}
              className="px-3 py-2 text-sm border rounded-md"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {permRegisterId && (
        <div className="border rounded-lg bg-white p-4 space-y-3">
          <div className="flex justify-between gap-2">
            <h3 className="font-medium">
              Permisos ·{' '}
              {registers.find((r) => r.id === permRegisterId)?.name ||
                registers.find((r) => r.id === permRegisterId)?.code}
            </h3>
            <button
              type="button"
              className="text-sm text-slate-600 hover:underline"
              onClick={() => setPermRegisterId(null)}
            >
              Cerrar
            </button>
          </div>
          <p className="text-xs text-slate-500">
            Reutiliza el acceso por caja existente. También se puede administrar por usuario en{' '}
            <Link to="/users" className="text-blue-700 hover:underline">
              Usuarios
            </Link>
            .
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Usuario</label>
              <select
                value={permUserId}
                onChange={(e) => setPermUserId(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Seleccionar…</option>
                {staff.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.email})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-3 content-end">
              {PERM_FLAGS.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!permFlags[f.key]}
                    onChange={(e) =>
                      setPermFlags({ ...permFlags, [f.key]: e.target.checked })
                    }
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
          <button
            type="button"
            disabled={busy || !permUserId}
            onClick={() => void savePermission()}
            className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md disabled:opacity-50"
          >
            Guardar permisos
          </button>
          {permRows.length > 0 && (
            <ul className="text-sm divide-y border rounded-md">
              {permRows.map((p) => (
                <li key={p.id || `${p.userId}-${p.cashRegisterId}`} className="px-3 py-2">
                  {personName(p.user)} ·{' '}
                  {[
                    p.canMove && 'operar',
                    p.canOpen && 'abrir',
                    p.canClose && 'cerrar',
                    p.canTransfer && 'transferir',
                    p.canApprove && 'aprobar',
                    p.canAudit && 'arqueo',
                    p.canAdjust && 'ajustes',
                  ]
                    .filter(Boolean)
                    .join(', ') || 'sin flags'}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : (
        <div className="space-y-6">
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
              Tesorería Central
            </h3>
            {renderTable(grouped.central, 'Sin Tesorería Central activa/inactiva.')}
          </section>
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
              Cajas Madre
            </h3>
            {renderTable(grouped.madres, 'Sin Cajas Madre.')}
          </section>
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
              Cajas Operativas
            </h3>
            {renderTable(grouped.ops, 'Sin cajas operativas.')}
          </section>
        </div>
      )}

      <p className="text-xs text-slate-500">
        No hay borrado físico de cajas con movimientos: usá <strong>Desactivar</strong>. Sucursales
        nuevas se crean en{' '}
        <Link to="/branches" className="text-blue-700 hover:underline">
          Sucursales y Cajas
        </Link>
        .
      </p>
    </div>
  );
}
